import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  Tooltip,
  Avatar,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownloadOutlined";

import LoadingModal from "components/common/loading/LoadingModal";
import ToggleButtons from "components/common/toggle_button/ToggleButton";
import ConfirmableToggleButtons from "components/common/toggle_button/ConfirmableToggleButtons";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";

import {
  useDownloadReportMutation,
  useHandleOruDftMutation,
  useSetUpdateAlertStatusMutation,
  useSetNurseFLagMutation,
  useSetAnticoagStatusMutation,
  useHandleOruForIntergyAccountMutation,
  useSendReportToSFTPInterfaceMutation,
  useUpdateLaaoDeviceMutation,
} from "app/store/transmission/api/transmissionDetailApi";

import {
  toggleClinicEsig,
  toggleFetchTableData,
  toggleNurseFlag,
  toggleAlertStatus,
  setCacheRefresh,
  toggleAttentionStatus,
  selectedReport,
  toggleAnticoagInformation,
  toggleIsUpdatingReportNotes,
  changeProfileField,
} from "app/store/transmission/slice/transmissionSlice";
import {
  CLINIC_ROLES,
  REPORT_SEVERITY,
  HF_BILLING_CODES,
  ALERT_STATUS_MAP,
} from "static/constants";
import ReportTrends from "../trends/ReportTrends";
import { MAP_PREFERENCE_USER_ROLES } from "static/preferences";
import { getRolePreference } from "utils/accountPreference";
import { getCurrentUserRole } from "utils/currentUser.util";
import useReportNotes from "../notes/report_notes/useReportNotes";
import AnticoagulantHistory from "components/common/anticoagulant-history/AnticoagulantHistory";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import RefusesOAC from "components/common/refusesOAC/RefusesOAC";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { transmissionTableApi } from "app/store/transmission/api/transmissionTableApi";
import { generateAvatar } from "app/utils/avatar";

const ReportHeaderButton = ({ data, activeUsers }) => {
  const [isSendingOruDft, setIsSendingOruDft] = useState(false);
  const [confirmRefusesOAC, setConfirmRefusesOAC] = useState(false);
  const [isRefusesOACLoading, setIsRefusesOACLoading] = useState(false);

  const role = localStorage.getItem("role");
  const activeCount = activeUsers?.length;

  const { account } = useSelector((state) => state.accountPreference);
  const currentUserRole = getCurrentUserRole();
  const userRole = MAP_PREFERENCE_USER_ROLES[currentUserRole];

  const { handleReportNote } = useReportNotes(data, null);

  const {
    reportCurrentTab,
    nurseFlag,
    alertStatus,
    attentionStatus,
    reportId,
    pdfFileName,
    isLegacyReport,
    anticoagInformation,
    reportDetail,
    reportAccountDetail,
    isCrossFacilityReport,
    information,
  } = useSelector((state) => state.transmission);

  const { isArchiveReport } = useSelector((state) => state.archiveReport);
  const isIntergyAccount = reportAccountDetail?.settings?.intergy_account;
  const isSFTPAccount = reportAccountDetail?.settings?.sftp_account;

  const dispatch = useDispatch();

  const [setNurseFLag] = useSetNurseFLagMutation();
  const [updateLaaoDevice] = useUpdateLaaoDeviceMutation();
  const [setAnticoagStatus, status] = useSetAnticoagStatusMutation();
  const [setUpdateAlertStatus, updateAlertStatus] =
    useSetUpdateAlertStatusMutation();
  const [handleOruDft] = useHandleOruDftMutation();
  const [handleOruForIntergyAccount] = useHandleOruForIntergyAccountMutation();
  const [sendReportToSFTPInterface] = useSendReportToSFTPInterfaceMutation();

  const [downloadReport, { isLoading: isDownloading }] =
    useDownloadReportMutation();

  useEffect(() => {
    if (data) {
      dispatch(toggleNurseFlag(data?.nurse_flag));
      dispatch(toggleClinicEsig(data?.eSig ?? false));
      dispatch(toggleAlertStatus(data?.alert_flag));
      dispatch(
        toggleAnticoagInformation(
          data?.patient?.information?.anticoag_information
        )
      );
      dispatch(
        toggleAttentionStatus(
          data?.warning_flag ??
            data?.evokeNotes?.some((note) =>
              note.content?.some(({ alert }) => alert === REPORT_SEVERITY[2])
            )
        )
      );
    }
  }, [data, dispatch]);

  const updateAlerts = async (e, newAlert) => {
    const prevAlertStatus = alertStatus;
    const prevAttentionStatus = attentionStatus;

    try {
      dispatch(toggleIsUpdatingReportNotes(true));
      dispatch(toggleAlertStatus(newAlert === "alert"));
      dispatch(toggleAttentionStatus(newAlert === "attention"));

      const result = await setUpdateAlertStatus({
        reportId: data?._id,
        status: newAlert,
      }).unwrap();

      if (result) {
        await showSuccessToast(
          newAlert === "none"
            ? "Alert set to none"
            : `Report is flagged as ${newAlert}`
        );

        dispatch(transmissionTableApi.util.invalidateTags("transmissions"));

        const reportNotes = [...(data?.evokeNotes || [])];
        const updatedReportNotes = handleReportNoteAlertUpdates(
          newAlert,
          reportNotes
        );

        await handleReportNote({
          notes: { reportNotes: updatedReportNotes },
          isAlertStatusUpdate: true,
        });

        await dispatch(toggleFetchTableData(true));
        await dispatch(setCacheRefresh(true));

        const alertPayload = {
          alert_flag: newAlert === "alert",
          warning_flag: newAlert === "attention",
        };

        await dispatch(
          selectedReport({
            id: reportId,
            isLegacyReport: isLegacyReport,
            reportDetail: {
              ...reportDetail,
              ...alertPayload,
              nurse_flag: nurseFlag,
              evokeNotes: updatedReportNotes,
            },
          })
        );
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      dispatch(toggleAlertStatus(prevAlertStatus));
      dispatch(toggleAttentionStatus(prevAttentionStatus));
      showErrorToast(errorMessage);
    } finally {
      await dispatch(toggleFetchTableData(false));
      dispatch(toggleIsUpdatingReportNotes(false));
    }
  };

  const handleReportNoteAlertUpdates = (newAlert, reportNotes = []) => {
    let noAlertsInReportNotes = true;

    /**
     * newAlert === "attention", change "Critical" report notes to "Warning"
     * newAlert === "alert", change "Warning" report notes to "Critical"
     * newAlert === "none", change "Warning" and "Critical" report notes to "None"
     */
    const updatedReportNotes = reportNotes?.map((note) => {
      const updatedContent = note?.content?.map((content) => {
        const updatedAlert =
          ALERT_STATUS_MAP[content.alert]?.[newAlert] || content.alert;

        if (updatedAlert !== "None") {
          noAlertsInReportNotes = false;
        }

        return {
          ...content,
          alert: updatedAlert,
        };
      });

      return {
        ...note,
        content: updatedContent,
      };
    });

    /* If all report notes have severity "None", change all notes to whatever is selected as alert status */
    if (noAlertsInReportNotes && newAlert !== "none") {
      const finalAlert = newAlert === "alert" ? "Critical" : "Warning";
      updatedReportNotes.forEach((note) => {
        note.content.forEach((content) => (content.alert = finalAlert));
      });
    }

    return updatedReportNotes;
  };

  const toggleNurseFlagStatus = async (e, c) => {
    try {
      dispatch(toggleNurseFlag(c));
      const result = await setNurseFLag({
        reportId: data?._id,
        status: !nurseFlag,
        account: data?.account,
      }).unwrap();

      if (result) {
        showSuccessToast("Nurse flag status updated successfully.");
        dispatch(transmissionTableApi.util.invalidateTags(["transmissions"]));
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      showErrorToast(errorMessage);
    }
  };

  const handleLaaoDeviceToggle = async (value) => {
    try {
      const informationData = { ...information, laao_device: value };

      dispatch(
        changeProfileField({
          type: "information",
          value: { ...informationData },
        })
      );

      const result = await updateLaaoDevice({
        patientId: data?.patient?._id,
        status: !information?.laao_device,
      }).unwrap();

      if (result) {
        showSuccessToast(result?.message);
        dispatch(transmissionTableApi.util.invalidateTags("transmissions"));
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      showErrorToast(errorMessage);
    }
  };

  const handleRefusesOAC = async (
    refusesOACStatus,
    removeAnticoagStatus = false
  ) => {
    let payload = { refusesOACStatus };

    // set anticoagStatus to "no" if refusesOAC is being set to true
    if (removeAnticoagStatus) {
      payload = { ...payload, status: "no" };
    }
    await updateAnticoagStatus(payload);
    setConfirmRefusesOAC(false);
  };

  const updateAnticoagStatus = async ({ status, refusesOACStatus }) => {
    try {
      setIsRefusesOACLoading(true);
      const anticoagStatus =
        status || anticoagInformation?.anticoag_status || "unknown";
      const result = await setAnticoagStatus({
        patientId: data?.patient?._id,
        status: anticoagStatus,
        account: data?.account,
        refusesOAC:
          refusesOACStatus === undefined
            ? anticoagInformation?.refusesOAC
            : refusesOACStatus,
      }).unwrap();

      if (result) {
        dispatch(toggleAnticoagInformation(result?.data));
        showSuccessToast(result?.message);
        dispatch(transmissionTableApi.util.invalidateTags("transmissions"));
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      showErrorToast(errorMessage);
    } finally {
      setIsRefusesOACLoading(false);
    }
  };

  const handleSendOruDft = async (type) => {
    try {
      setIsSendingOruDft(true);
      let result;
      if (isSFTPAccount) {
        result = await sendReportToSFTPInterface(data?._id).unwrap();

        showSuccessToast(result?.message);
      } else {
        if (isIntergyAccount) {
          result = await handleOruForIntergyAccount(data?._id).unwrap();
        } else {
          result = await handleOruDft(data?._id).unwrap();
        }

        if (result) {
          showSuccessToast(`Sending ${type} requested successfully.`);
        }
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      showErrorToast(errorMessage);
    } finally {
      setIsSendingOruDft(false);
    }
  };

  const handleDownloadReport = async () => {
    if (pdfFileName) {
      try {
        let filters = {
          isFetchingCoverPage: !isLegacyReport,
        };

        if (HF_BILLING_CODES.includes(reportDetail?.billing_code)) {
          filters.isHFReport = true;
        }

        const result = await downloadReport({
          reportId,
          filters,
        }).unwrap();

        if (result) {
          showSuccessToast("Report downloaded successfully.");
        }
      } catch (error) {
        const errorMessage = error?.message || error?.data?.message;

        showErrorToast(errorMessage);
      }
    }
  };

  const handleAlertChange = (event, newAlert) => {
    newAlert && updateAlerts(event, newAlert);
  };

  const OruDftButton = () => {
    const hl7Configs = reportAccountDetail?.settings?.hl7_configs;

    const actionType =
      hl7Configs?.oru && hl7Configs?.dft
        ? "Export & Bill to EHR"
        : hl7Configs?.dft
          ? "Bill to EHR"
          : "Export to EHR";

    return (
      (hl7Configs?.oru || hl7Configs?.dft) &&
      hl7Configs?.allowed_tabs?.includes(reportCurrentTab) && (
        <Button
          onClick={() => handleSendOruDft(actionType)}
          variant="outlined"
          color="secondary"
          disabled={isSendingOruDft}
        >
          {actionType}
          {isSendingOruDft && <CircularProgress size="14px" className="ml-2" />}
        </Button>
      )
    );
  };

  const renderDivider = (
    <Divider
      orientation="vertical"
      flexItem
      style={{ marginLeft: "4px", marginRight: "4px" }}
    />
  );

  return (
    <>
      <div
        className="d-flex align-items-center flex-end"
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{ gap: "10px" }}
      >
        {/* Show active users if more than 1 user is present as first user wil be the current user */}
        {activeUsers.length > 0 && (
          <Tooltip
            placement="bottom"
            slotProps={{
              tooltip: {
                sx: {
                  minWidth: "168px",
                  padding: "8px 12px",
                },
              },
            }}
            title={
              <div>
                {activeUsers.map((user, index) => (
                  <div key={index} className="d-flex align-items-center mb-1">
                    <Avatar
                      sx={{
                        backgroundColor: "#eaf2ff",
                        color: "#175ec8",
                        height: "30px",
                        width: "30px",
                        marginRight: "8px",
                        fontSize: "12px",
                      }}
                      alt={user?.name}
                    >
                      {generateAvatar(user?.name)}
                    </Avatar>
                    {user?.name}
                  </div>
                ))}
              </div>
            }
          >
            <Chip
              style={{ background: "#EAF2FF", color: "#175EC8" }}
              icon={<RemoveRedEyeOutlinedIcon color="#175EC8" />}
              label={`${activeCount} Active`}
            />
            {renderDivider}
          </Tooltip>
        )}
        <FormControlLabel
          label={
            <>
              LAAO <br /> Device
            </>
          }
          labelPlacement="start"
          control={
            <Switch
              name="laao_device"
              checked={information?.laao_device}
              onChange={(_event, checked) => handleLaaoDeviceToggle(checked)}
            />
          }
          disabled={isCrossFacilityReport}
          className="toggle-label"
          style={{
            marginLeft: 0,
          }}
        />

        {renderDivider}

        {!data?.legacy &&
          getRolePreference(
            account,
            "transmission_report",
            "nurse_flag"
          )?.includes(userRole) &&
          reportAccountDetail?.settings?.nurse_flag && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    onChange={toggleNurseFlagStatus}
                    checked={nurseFlag}
                    name={"nurse_flag"}
                    value={"nurse_flag"}
                  />
                }
                label={"NF"}
                labelPlacement="start"
                style={{ marginLeft: 0 }}
              />
              {renderDivider}
            </>
          )}

        {!isArchiveReport && (
          <>
            {getRolePreference(
              account,
              "transmission_report",
              "anticoag_status"
            )?.includes(userRole) && (
              <>
                <RefusesOAC
                  label={
                    <>
                      Refuses <br /> OAC
                    </>
                  }
                  disable={status.isLoading || isCrossFacilityReport}
                  refusesOAC={anticoagInformation?.refuses_oac}
                  anticoagStatus={anticoagInformation?.anticoag_status}
                  handleRefuseOAC={(refusesOAC, removeAnticoagStatus) => {
                    handleRefusesOAC(refusesOAC, removeAnticoagStatus);
                  }}
                />
                <span className="anticoag-status-title">
                  OAC <br /> Status
                </span>
                {(anticoagInformation?.updated_at ||
                  anticoagInformation?.updated_by?.name) && (
                  <AnticoagulantHistory
                    updatedAt={anticoagInformation?.updated_at}
                    updatedBy={anticoagInformation?.updated_by?.name}
                  />
                )}

                <ConfirmableToggleButtons
                  value={anticoagInformation?.anticoag_status || "unknown"}
                  color="info"
                  disabled={status.isLoading || isCrossFacilityReport}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    updateAnticoagStatus({
                      status: newValue,
                      refusesOACStatus:
                        anticoagInformation?.refuses_oac && newValue === "no",
                    });
                  }}
                  showConfirmWhen={(newValue) =>
                    anticoagInformation?.refuses_oac && newValue !== "no"
                  }
                  confirmTitle="Confirm Status Change"
                  confirmMessage={
                    <>
                      Changing the status will update the <b>OAC Status</b> and
                      remove the <b>Refuses OAC</b>. Are you sure you want to
                      continue?
                    </>
                  }
                  confirmButtonText="Confirm"
                  cancelButtonText="Cancel"
                  buttons={[
                    { value: "yes", color: "success", label: "Yes" },
                    { value: "no", color: "error", label: "No" },
                    { value: "unknown", label: "Unknown" },
                  ]}
                />

                {renderDivider}
              </>
            )}

            {!data?.legacy && (
              <>
                {CLINIC_ROLES.includes(currentUserRole) && (
                  <>
                    <span className="alert-status-title">
                      Alert <br /> Status
                    </span>
                    <ToggleButtons
                      value={
                        alertStatus
                          ? "alert"
                          : attentionStatus
                            ? "attention"
                            : "none"
                      }
                      disabled={updateAlertStatus.isLoading}
                      onChange={handleAlertChange}
                      buttons={[
                        {
                          value: "none",
                          color: "success",
                          label: "None",
                          style:
                            !alertStatus && !attentionStatus
                              ? {
                                  color: "white",
                                  backgroundColor: "rgb(76, 175, 80)",
                                }
                              : { borderColor: "rgba(0, 0, 0, 0.12)" },
                        },
                        {
                          value: "attention",
                          color: "warning",
                          label: "Attention",
                          style:
                            !alertStatus && attentionStatus
                              ? {
                                  color: "white",
                                  backgroundColor: "rgb(255, 152, 0)",
                                }
                              : { borderColor: "rgba(0, 0, 0, 0.12)" },
                        },
                        {
                          value: "alert",
                          color: "error",
                          label: "Alert",
                          style: alertStatus
                            ? {
                                color: "white",
                                backgroundColor: "rgb(239, 83, 80)",
                              }
                            : { borderColor: "rgba(0, 0, 0, 0.12)" },
                        },
                      ]}
                    />

                    {renderDivider}
                  </>
                )}
              </>
            )}
          </>
        )}

        <ReportTrends
          className="trends_button"
          patientId={data?.patient?._id}
        />

        {!isArchiveReport && (role === "admin" || role === "clinic") && (
          <OruDftButton />
        )}

        {isDownloading && (
          <LoadingModal open={isDownloading} loadingText="Downloading..." />
        )}
        <Button
          onClick={() => handleDownloadReport()}
          className="view-pdf__button"
          variant="outlined"
          color="secondary"
          disabled={!pdfFileName || isDownloading}
        >
          <FileDownloadIcon />
        </Button>

        {confirmRefusesOAC && (
          <ConfirmModal
            confirmText="Confirm"
            title="Confirm Refuses OAC"
            show={confirmRefusesOAC}
            isConfirming={isRefusesOACLoading}
            onConfirm={() =>
              handleRefusesOAC(!anticoagInformation?.refusesOAC, true)
            }
            onCancel={() => setConfirmRefusesOAC(false)}
            children={
              <span>
                Updating this will automatically set{" "}
                <b>Anticoagulants Status</b> to <b>"No"</b>. Are you sure you
                want to confirm this change?
              </span>
            }
          />
        )}
      </div>
    </>
  );
};

export default ReportHeaderButton;
